/**
 * Table chrome for **Reports** (`/reports`).
 *
 * These are Settings → Team's table values (`Team.tokens.ts`): the same 14px
 * card and `#E9E7F1` hairline, the same `#FAF9FC` header strip, the same 28px
 * gutter, 10px header padding and 13px row padding, the same `#F2F0F7` row
 * rule and the same 10.5px/13px type steps. `Reports.dc.html` draws a warmer,
 * roomier table of its own (a 16px card, a `#FDF3EE` pink strip, 26px/16px/18px
 * spacing) but the app's tables are meant to read as one set, so the mock's
 * chrome is deliberately not followed here.
 *
 * They live in this file rather than in `Team.tokens.ts` because that file is
 * screen-scoped and temporary; when the settings screens are all re-skinned and
 * these values are promoted into the MUI theme, both copies go away together.
 *
 * The values reach the shared `CustomTable` through its `cardSx` /
 * `tableSxOverride` props, so no other table in the app is touched.
 */

import { contentCard, signer, white } from "theme/colors";

export const reportsTableTokens = {
  card: {
    radius: "14px",
    border: contentCard.borderMuted,
    borderWidth: "1px",
    shadow: "0 1px 3px rgba(33, 21, 71, 0.04)",
    surface: white,
  },

  /** Horizontal padding shared by the header strip and every row. */
  gutter: "28px",

  header: {
    bg: signer.surface.subtle,
    borderBottom: signer.border.light,
    paddingY: "10px",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: signer.text.faint,
  },

  row: {
    paddingY: "13px",
    divider: signer.border.divider,
    /* Team's rows are inert; these two are Reports-only affordances, drawn
       from the same palette so they read as part of the set. */
    hoverBg: signer.surface.subtle,
    selectedBg: signer.surface.muted,
    /** Leading identity cell. */
    nameFontSize: "13px",
    nameFontWeight: 600,
    nameColor: signer.ink,
    subFontSize: "12px",
    subColor: signer.text.meta,
    subMarginTop: "3px",
    /** Numeric cells. */
    valueFontSize: "13px",
    valueFontWeight: 600,
    valueColor: signer.ink,
    /** Completion-rate column reads one step stronger. */
    strongFontSize: "13px",
    strongFontWeight: 700,
    strongColor: signer.ink,
  },

  /**
   * The selection column. The control itself is the app's shared checkbox, so
   * only the track it sits in is set here — 20px, matching the 18px glyph
   * `CustomTable` renders with no padding around it.
   */
  checkbox: {
    columnWidth: "20px",
  },

  /** Gap between the checkbox column and the first data column. */
  columnGap: "18px",
} as const;

export default reportsTableTokens;
